import { Link } from 'react-router-dom';
import { type Product } from '@/mocks/products';
import { useCart } from '@/context/CartContext';

interface ProductCardProps {
  product: Product;
  variant?: 'default' | 'bestseller' | 'compact';
}

export default function ProductCard({ product, variant = 'default' }: ProductCardProps) {
  const { addItem } = useCart();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.variants && product.variants.length > 0) {
      addItem(product, 1, product.variants[0].id);
    } else {
      addItem(product);
    }
  };

  const displayPrice = product.variants
    ? Math.min(...product.variants.map(v => v.price))
    : product.price;

  const hasVariants = product.variants && product.variants.length > 0;

  return (
    <Link
      to={`/product/${product.slug}`}
      className={`group block card-hover-lift ${variant === 'compact' ? '' : ''}`}
    >
      <div className="bg-secondary-100 rounded-2xl overflow-hidden relative">
        {/* Image */}
        <div className="aspect-square overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {product.isNew && (
            <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm">
              NEW ARRIVAL
            </span>
          )}
          {product.isTrending && (
            <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm flex items-center gap-1">
              <i className="ri-fire-line text-[11px]"></i>
              TRENDING
            </span>
          )}
          {product.isBestseller && (
            <span className="px-2.5 py-1 bg-[#D9A441] text-background-50 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm flex items-center gap-1">
              <i className="ri-award-line text-[11px]"></i>
              BESTSELLER
            </span>
          )}
          {product.mrp && product.mrp > product.price && (
            <span className="px-2.5 py-1 bg-accent-500/90 text-background-50 text-[10px] font-bold rounded-full">
              SAVE Rs.{product.mrp - product.price}
            </span>
          )}
        </div>

        {/* Quick add to cart overlay */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={handleAddToCart}
            className="w-9 h-9 flex items-center justify-center bg-accent-500 text-background-50 rounded-full hover:bg-accent-600 transition-all duration-200 paw-bounce shadow-sm"
            aria-label={`Add ${product.name} to cart`}
          >
            <i className="ri-add-line text-lg paw-icon"></i>
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="mt-3 px-0.5">
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          <span className="px-2 py-0.5 bg-primary-50 text-primary-600 text-[10px] font-semibold rounded-full">
            {product.petType === 'dog' ? '🐕 Dog' : product.petType === 'cat' ? '🐈 Cat' : '🐾 Universal'}
          </span>
          <span className="px-2 py-0.5 bg-secondary-100 text-foreground-600 text-[10px] font-medium rounded-full">
            {product.functionCategory}
          </span>
        </div>

        <h3 className="text-sm font-bold text-foreground-800 leading-snug mb-1 line-clamp-2 font-heading">
          {product.name}
        </h3>

        <p className="text-xs text-foreground-500 mb-2 line-clamp-1">{product.materialClaims[0]}</p>

        <div className="flex items-center gap-1.5 mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <i
                key={i}
                className={`text-[10px] ${i < Math.floor(product.rating) ? 'ri-star-fill text-[#D9A441]' : 'ri-star-line text-foreground-300'}`}
              ></i>
            ))}
          </div>
          <span className="text-[10px] text-foreground-400">({product.reviewCount})</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-foreground-800">Rs.{displayPrice}</span>
          {product.mrp && product.mrp > displayPrice && (
            <span className="text-xs text-foreground-400 line-through">Rs.{product.mrp}</span>
          )}
          {hasVariants && (
            <span className="text-[10px] text-foreground-500 ml-auto">+ options</span>
          )}
        </div>

        {/* Add to Cart full button visible on mobile (no hover) */}
        <button
          onClick={handleAddToCart}
          className="mt-3 w-full py-2.5 md:hidden bg-accent-500 text-background-50 rounded-full font-semibold text-xs hover:bg-accent-600 transition-colors duration-200 flex items-center justify-center gap-1.5"
        >
          <i className="ri-add-line"></i>
          Add to Cart
        </button>
      </div>
    </Link>
  );
}